export interface ProfitInputs {
  unitPrice: number;
  dailyGuests: number;
  businessDays: number;
  laborCost: number;
  foodCostRate: number;
  rent: number;
  otherFixedCost: number;
  guestMode: 'direct' | 'seats';
  seats: number;
  occupancyRate: number;
  rotations: number;
}

export const exampleProfitInputs: ProfitInputs = {
  unitPrice: 3500,
  dailyGuests: 25,
  businessDays: 26,
  laborCost: 1_200_000,
  foodCostRate: 27,
  rent: 360_000,
  otherFixedCost: 500_000,
  guestMode: 'direct',
  seats: 42,
  occupancyRate: 20,
  rotations: 2,
};

export const CREDIT_CARD_SHARE = 0.6;
export const CREDIT_CARD_FEE_RATE = 0.04;
export const SIMPLE_TAX_RATE = 0.1;

export interface ProfitResult {
  dailyGuests: number;
  monthlyGuests: number;
  sales: number;
  foodCost: number;
  grossProfit: number;
  fixedCost: number;
  cardSales: number;
  cardFee: number;
  cardNetReceipts: number;
  estimatedConsumptionTax: number;
  operatingProfit: number;
  profitMargin: number | null;
  monthlyBalance: number;
  balanceMargin: number | null;
  breakEvenSales: number | null;
  salesGap: number | null;
  requiredDailyGuests: number | null;
  additionalDailyGuests: number | null;
  additionalWholeGuests: number | null;
  requiredUnitPrice: number | null;
  fixedCostReduction: number;
}

const nonNegative = (value: number): number => Number.isFinite(value) ? Math.max(0, value) : 0;

export const calculateProfit = (inputs: ProfitInputs): ProfitResult => {
  const unitPrice = nonNegative(inputs.unitPrice);
  const businessDays = Math.min(31, nonNegative(inputs.businessDays));
  const foodCostRate = Math.min(100, nonNegative(inputs.foodCostRate)) / 100;
  const dailyGuests = inputs.guestMode === 'seats'
    ? nonNegative(inputs.seats) * Math.min(100, nonNegative(inputs.occupancyRate)) / 100 * nonNegative(inputs.rotations)
    : nonNegative(inputs.dailyGuests);
  const monthlyGuests = dailyGuests * businessDays;
  const sales = unitPrice * monthlyGuests;
  const foodCost = sales * foodCostRate;
  const grossProfit = sales - foodCost;
  const fixedCost = nonNegative(inputs.laborCost) + nonNegative(inputs.rent) + nonNegative(inputs.otherFixedCost);
  const cardSales = sales * CREDIT_CARD_SHARE;
  const cardFee = cardSales * CREDIT_CARD_FEE_RATE;
  const cardNetReceipts = cardSales - cardFee;
  const estimatedConsumptionTax = grossProfit * SIMPLE_TAX_RATE;
  const operatingProfit = grossProfit - fixedCost - cardFee;
  const monthlyBalance = operatingProfit - estimatedConsumptionTax;
  const contributionRate = (1 - foodCostRate) * (1 - SIMPLE_TAX_RATE)
    - CREDIT_CARD_SHARE * CREDIT_CARD_FEE_RATE;
  const breakEvenSales = contributionRate > 0 ? fixedCost / contributionRate : null;
  const requiredDailyGuests = breakEvenSales !== null && unitPrice > 0 && businessDays > 0
    ? breakEvenSales / unitPrice / businessDays : null;
  const additionalDailyGuests = requiredDailyGuests === null ? null : Math.max(0, requiredDailyGuests - dailyGuests);

  return {
    dailyGuests,
    monthlyGuests,
    sales,
    foodCost,
    grossProfit,
    fixedCost,
    cardSales,
    cardFee,
    cardNetReceipts,
    estimatedConsumptionTax,
    operatingProfit,
    profitMargin: sales > 0 ? operatingProfit / sales * 100 : null,
    monthlyBalance,
    balanceMargin: sales > 0 ? monthlyBalance / sales * 100 : null,
    breakEvenSales,
    salesGap: breakEvenSales === null ? null : Math.max(0, breakEvenSales - sales),
    requiredDailyGuests,
    additionalDailyGuests,
    additionalWholeGuests: additionalDailyGuests === null ? null : Math.ceil(Math.max(0, additionalDailyGuests - 1e-9)),
    requiredUnitPrice: breakEvenSales !== null && monthlyGuests > 0 ? Math.ceil(breakEvenSales / monthlyGuests) : null,
    fixedCostReduction: Math.max(0, -monthlyBalance),
  };
};
